<template>
  <div>
    <div
      class="digital-time-picker"
      @keydown="meridianKeyPressHandler"
      @click.stop
    >
      <input
        :id="`hour-input-${uuid}`"
        ref="hourInputRef"
        :value="hour"
        type="text"
        class="time-input hour-input"
        placeholder="00"
        @keydown="hourKeyPressHandler"
        @input="handleHourInput"
      />
      <div class="time-separator">
        <div class="dot" />
        <div class="dot" />
      </div>
      <input
        :id="`minute-input-${uuid}`"
        ref="minuteInputRef"
        :value="minute"
        type="text"
        class="time-input minute-input"
        placeholder="00"
        @keydown="minuteKeyPressHandler"
        @input="handleMinuteInput"
      />
      <div class="meridian-buttons">
        <button
          type="button"
          class="meridian-button meridian-button--top"
          :class="{
            'meridian-button--active': isAM,
            'meridian-button--inactive': !isAM,
          }"
          @click="setToAM"
        >
          AM
        </button>
        <button
          type="button"
          class="meridian-button meridian-button--bottom"
          :class="{
            'meridian-button--active': isPM,
            'meridian-button--inactive': !isPM,
          }"
          @click="setToPM"
        >
          PM
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { KeyCode } from '../../util/enum'
import {
  getMeridianFrom24Hour,
  hours12To24Number,
  hours24To12Number,
} from '../../util/timeStrings'
import { generateShortUUID } from '../../util/id'

// Simple input masking function
function applyMask(value: string, maxLength: number = 2): string {
  // Remove non-digits and limit length
  const digits = value.replace(/\D/g, '')
  if (digits.startsWith('0')) {
    return digits.slice(-maxLength)
  }
  return digits.slice(0, maxLength)
}
// Props
const props = defineProps<{
  value: string | null
}>()

// Emits
const emit = defineEmits<{
  (e: 'input', value: string | null): void
}>()

// Reactive state
const isAM = ref(true)
const isPM = ref(false)
const hour = ref<string | null>(null)
const minute = ref<string | null>(null)
const uuid = ref('')

// Template refs
const hourInputRef = ref<HTMLInputElement>()
const minuteInputRef = ref<HTMLInputElement>()

// Computed properties
const hours24 = computed((): number | null => {
  if (hour.value === null) {
    return null
  }
  return hours12To24Number(parseInt(hour.value), isAM.value ? 'AM' : 'PM')
})

const isoTime = computed((): string | null => {
  if (
    (hours24.value === null && hours24.value !== 0) ||
    (!minute.value && parseInt(minute.value || '') !== 0)
  ) {
    return null
  }
  const hourStr = hours24.value! < 10 ? `0${hours24.value}` : `${hours24.value}`
  const minuteStr = minute.value || '00'
  return `${hourStr}:${minuteStr}:00`
})

// Watchers
watch(isoTime, (value) => {
  emit('input', value)
})

watch(
  () => props.value,
  (value) => {
    updateInputsFromValueChange(value)
  },
)

// Lifecycle
onMounted(() => {
  uuid.value = generateShortUUID()
  if (props.value) {
    updateInputsFromValueChange(props.value)
  }
})

// Methods
function setToAM(): void {
  isAM.value = true
  isPM.value = false
}

function setToPM(): void {
  isPM.value = true
  isAM.value = false
}

function toggleMeridian(): void {
  if (isAM.value) {
    setToPM()
  } else {
    setToAM()
  }
}

function hourKeyPressHandler(event: KeyboardEvent): void {
  const keyCode = event.keyCode

  if (keyCode === KeyCode.Colon) {
    event.preventDefault()
    minuteInputRef.value?.focus()
    return
  }

  if (keyCode === KeyCode.UpArrow) {
    event.preventDefault()
    const current = parseInt(hour.value || '12')
    setHourValue(current + 1)
    return
  }

  if (keyCode === KeyCode.DownArrow) {
    event.preventDefault()
    const current = parseInt(hour.value || '1')
    setHourValue(current - 1)
    return
  }

}

function minuteKeyPressHandler(event: KeyboardEvent): void {
  const keyCode = event.keyCode

  if (keyCode === KeyCode.UpArrow) {
    event.preventDefault()
    const current = parseInt(minute.value || '59')
    setMinuteValue(current + 1)
    return
  }

  if (keyCode === KeyCode.DownArrow) {
    event.preventDefault()
    const current = parseInt(minute.value || '0')
    setMinuteValue(current - 1)
    return
  }

}

function handleHourInput(event: Event): void {
  const target = event.target as HTMLInputElement
  const value = target.value

  // Apply masking first (digits only, max 2 chars)
  const maskedValue = applyMask(value, 2)

  // If masking changed the value, update the input
  if (maskedValue !== value) {
    target.value = maskedValue
  }

  if (maskedValue === '') {
    hour.value = null
    target.value = ''
    return
  }

  const numericValue = parseInt(maskedValue)
  if (isNaN(numericValue) || numericValue < 1 || numericValue > 12) {
    hour.value = null
    target.value = ''
    return
  }

  hour.value = String(numericValue)
  target.value = hour.value
}

function handleMinuteInput(event: Event): void {
  const target = event.target as HTMLInputElement
  const value = target.value

  // Apply masking first (digits only, max 2 chars)
  const maskedValue = applyMask(value, 2)

  // If masking changed the value, update the input
  if (maskedValue !== value) {
    target.value = maskedValue
  }

  // Parse and validate range for direct input
  if (maskedValue === '') {
    minute.value = null
    target.value = ''
    return
  }

  const numericValue = parseInt(maskedValue)
  if (isNaN(numericValue) || numericValue < 0 || numericValue > 59) {
    // Invalid value - clear the input
    minute.value = null
    target.value = ''
    return
  }

  // Valid value - set it with proper formatting
  const formattedValue = numericValue < 10 ? `0${numericValue}` : String(numericValue)
  minute.value = formattedValue
  target.value = formattedValue
}

function setHourValue(valueToSet: number | null): void {
  const element = hourInputRef.value
  const value = valueToSet !== null ? valueToSet : element?.value

  if (value === '' || value === null || value === undefined) {
    hour.value = null
    return
  }

  const intValue = parseInt(String(value))
  if (isNaN(intValue)) {
    if (element) element.value = hour.value || ''
    return
  }

  if (String(value).length > 2) {
    return
  }

  let finalValue = intValue

  // Handle wrapping and AM/PM toggle
  if (intValue > 12) {
    finalValue = 1
    toggleMeridian()
  } else if (intValue < 1) {
    finalValue = 12
    toggleMeridian()
  }

  hour.value = String(finalValue)
  if (element) element.value = hour.value
}

function setMinuteValue(valueToSet: number | null): void {
  const element = minuteInputRef.value
  const value = valueToSet !== null ? valueToSet : element?.value

  if (value === '' || value === null || value === undefined) {
    minute.value = null
    return
  }

  const intValue = parseInt(String(value))
  if (isNaN(intValue)) {
    if (element) element.value = minute.value || ''
    return
  }

  let finalValue = intValue

  // Handle wrapping and hour adjustment
  if (intValue > 59) {
    finalValue = 0
    const currentHour = parseInt(hour.value || '12')
    setHourValue(currentHour + 1)
  } else if (intValue < 0) {
    finalValue = 59
    const currentHour = parseInt(hour.value || '1')
    setHourValue(currentHour - 1)
  }

  const formattedValue = finalValue < 10 ? `0${finalValue}` : String(finalValue)

  minute.value = formattedValue
  if (element) element.value = formattedValue
}

function meridianKeyPressHandler(event: KeyboardEvent): void {
  const keyCode = event.keyCode

  // Don't update AM/PM if modifier keys are pressed (Cmd, Ctrl, Alt, Shift)
  // Prevents updating the time if we're selecting an input with Ctrl+A or similar
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
    return
  }

  if (keyCode === KeyCode.AKey) {
    setToAM()
    return
  }

  if (keyCode === KeyCode.PKey) {
    setToPM()
    return
  }
}

function updateInputsFromValueChange(value: string | null): void {
  if (!value) return

  const splitValue = value.split(':')
  const newHour = parseInt(splitValue[0])
  const newHour12Hour = hours24To12Number(newHour)
  const newMinute = parseInt(splitValue[1])
  const oldHour = parseInt(hour.value || '0')
  const oldMinute = minute.value === null ? null : parseInt(minute.value)

  if (newHour12Hour !== oldHour) {
    setHourValue(newHour12Hour)
    if (getMeridianFrom24Hour(newHour) === 'AM') {
      setToAM()
    } else {
      setToPM()
    }
  }

  if (minute.value === null || newMinute !== oldMinute) {
    setMinuteValue(newMinute)
  }
}
</script>

<style scoped lang="scss">
.digital-time-picker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.time-input {
  font-size: 32px;
  font-weight: 500;
  width: 80px;
  height: 64px;
  border: 2px solid var(--cu-gray-100);
  border-radius: 8px;
  text-align: center;
  color: var(--cu-blue-primary);
  background: white;

  &:focus {
    outline: none;
    border: 2px solid var(--cu-blue-primary) !important;
    color: var(--cu-blue-primary);
  }

  &::placeholder {
    color: var(--cu-gray-400, #9ca3af);
  }
}

.time-separator {
  display: flex;
  flex-direction: column;
  margin: 0 8px;
  gap: 8px;
}

.dot {
  background-color: var(--cu-blue-primary);
  height: 6px;
  width: 6px;
  border-radius: 50%;
}

.meridian-buttons {
  display: flex;
  flex-direction: column;
  margin-left: 8px;
  font-weight: 500;
}

.meridian-button {
  padding: 4px 8px;
  font-size: 14px;
  border: 2px solid;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;

  &--top {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border-bottom: 1px solid;
  }

  &--bottom {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    border-top: 1px solid;
    margin-top: -2px;
  }

  &--active {
    color: var(--cu-blue-primary);
    border-color: var(--cu-blue-primary);
    background-color: var(--cu-blue-10);
    z-index: 2;
    position: relative;
  }

  &--inactive {
    border-color: var(--cu-gray-100);
    color: var(--cu-gray-400, #9ca3af);
  }

  &:hover:not(&--active) {
    border-color: var(--cu-gray-400, #9ca3af);
  }
}

/* Remove spinner arrows from number inputs */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>
